import { v4 as uuid } from 'uuid';

import type { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { HiddenPart } from 'src/containers/ProjectDiagram/templates/extensions/hiddenPart';
import { ExtendedLink } from 'src/containers/ProjectDiagram/templates/extensions/link';
import { ExtendedNode } from 'src/containers/ProjectDiagram/templates/extensions/node';
import {
  FileUpload,
  Mapping,
  MappingLabel,
  ModelConceptClass,
  ModelConceptHierarchy,
  ModelConceptHierarchyLabel,
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
  Resource,
  VirtualGraph,
} from 'src/containers/ProjectDiagram/types/data';
import {
  ModelSnapshot,
  createEmptyModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';

export interface ResourceNode extends ExtendedNode {
  key: string;
  data: Resource;
}

export interface FileUploadNode extends ResourceNode {
  key: string;
  data: FileUpload;
}

export interface VirtualGraphNode extends ResourceNode {
  key: string;
  data: VirtualGraph;
}

export interface MappingLabelNode extends ExtendedNode {
  key: string;
  data: MappingLabel;
  labeledLink: MappingLink | null;
}

export interface MappingLink extends ExtendedLink {
  key: string;
  data: Mapping;
  fromNode: ResourceNode | FileUploadNode | VirtualGraphNode;
  toNode: ModelConceptClassNode | ModelConceptRelationshipLabelNode | null;
  labelNodes: go.Iterator<MappingLabelNode>;
}

export interface ModelConceptClassNode extends ExtendedNode {
  key: string;
  data: ModelConceptClass;
  linksConnected: go.Iterator<ModelConceptRelationshipLink>;
}

export interface ModelConceptRelationshipLabelNode extends ExtendedNode {
  key: string;
  data: ModelConceptRelationshipLabel;
  labeledLink: ModelConceptRelationshipLink | null;
}

export interface ModelConceptRelationshipLink extends ExtendedLink {
  key: string;
  data: ModelConceptRelationship;
  fromNode: ModelConceptClassNode | null;
  labelNodes: go.Iterator<ModelConceptRelationshipLabelNode>;
  toNode: ModelConceptClassNode | null;
}

export interface ModelConceptHierarchyLabelNode extends ExtendedNode {
  key: string;
  data: ModelConceptHierarchyLabel;
  labeledLink: ModelConceptHierarchyLink | null;
}

export interface ModelConceptHierarchyLink extends ExtendedLink {
  key: string;
  data: ModelConceptHierarchy;
  fromNode: ModelConceptClassNode;
  labelNodes: go.Iterator<ModelConceptHierarchyLabelNode>;
  toNode: ModelConceptClassNode;
}

export type DiagramNode =
  | ResourceNode
  | FileUploadNode
  | VirtualGraphNode
  | ModelConceptClassNode;
export type DiagramLinkLabel =
  | MappingLabelNode
  | ModelConceptHierarchyLabelNode
  | ModelConceptRelationshipLabelNode;
export type DiagramLink =
  | MappingLink
  | ModelConceptHierarchyLink
  | ModelConceptRelationshipLink;
export type DiagramPart = HiddenPart;
export type DiagramGraphObject =
  | DiagramNode
  | DiagramLinkLabel
  | DiagramLink
  | DiagramPart;

export type Diagram = ExtendedDiagram;

export interface DiagramSnapshot {
  id: string;
  model: ModelSnapshot;
}

export const createEmptyDiagramSnapshot = (): DiagramSnapshot => ({
  id: uuid(),
  model: createEmptyModelSnapshot(),
});
